import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import {Checkbox, Form, Grid, Icon, Label, Radio, TextArea} from 'semantic-ui-react';
import {DateTimePicker} from 'react-widgets';
import {EmptyFunc, isFunction, range, reduce, replaceAll} from 'utils/DBUtils';
import omit from 'lodash/omit';
import isString from 'lodash/isString';
import forEach from 'lodash/forEach';

import 'react-tippy/dist/tippy.css';
import {Tooltip} from 'react-tippy';

import {validationRules} from 'components/forms/validationRules.js';
import {clone, isNoU, isUndefined} from "../../../../utils/DBUtils";
import {FormattedMessage} from "react-intl";
import FieldStatusIcon from 'components/forms/FieldStatusIcon';

/**
 * Utility methods to suppress warning when passing error prop to
 * element that does not except it.
 */
const compsWithNoError = [
    TextArea,
    Checkbox,
    Radio
];

function includeErrorForComponent(comp) {
    return compsWithNoError.indexOf(comp) <= -1;
}

const compsWithNoValue = [
    DateTimePicker
];

function includeValueForComponent(comp) {
    return compsWithNoValue.indexOf(comp) <= -1;
}

const compsWithCheckedValue = [
    Checkbox,
    Radio
];

function includeCheckedValueForComponent(comp) {
    return compsWithCheckedValue.indexOf(comp) > -1;
}

/**
 * This is the core of any auto-saving form field.  It can render as
 * an input, dropdown, radio button, etc.  Basically any input that
 * you can have in a Semantic UI React Form can be rendered here by
 * passing in the proper `control` prop.  While it may appear to do quite
 * a bit, this component should only have knowledge that it is rendering an input
 * of some sort and making an ajax call to save that field value based on some
 * input event such as `onChange` or `onBlur`.  It should not have any knowledge of
 * break logic or special break fields etc.
 **/

class AutoSaveField extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            hasBeenUpdated: false, // true if this component received a default value different than its original default value
            /** Represents whether or not this field is currently
             waiting on an ajax save request */
            isSaving: false,

            /** Represents whether or not this field contains valid input
             based on the rules defined in the `validationRules` prop */
            isValid: true,

            /** Represents the actual validation error to display */  //TODO: Possibly make this an array of error objects
            validationError: [],

            /** The current value of the field itself.  Need to maintain this
             in state in order to use the `value` prop on the various Semantic
             UI Form.Field components.  Initialized with the `defaultValue` prop */
            value: !isUndefined(props.defaultValue) ? props.defaultValue : '',
            checked: !isUndefined(props.defaultChecked) ? props.defaultChecked : false
        };
    }

    /** If a new value is passed in, we need to update our
     value in state to match so that it updates in the UI. */
    componentWillReceiveProps(nextProps) {
        let nextState = clone(this.state);
        let changes = {};
        if (this.props.defaultValue !== nextProps.defaultValue) {
            changes.value = nextProps.defaultValue;
            nextState.hasBeenUpdated = true;
        }
        if (this.props.defaultChecked !== nextProps.defaultChecked) {
            changes.checked = nextProps.defaultChecked;
            nextState.hasBeenUpdated = true;
        }

        if (nextProps.linkedErrors.length > 0) {
            changes.isValid = false;
        }

        if (nextProps.errors !== null && nextProps.errors.length > 0) {
            if (nextProps.isSaving) {
                changes.isValid = true;
            } else {
                changes.isValid = false;
            }
        }

        if (Object.keys(changes).length > 0) {
            this.setState(changes);
        }
    }

    /**
     * Whenever there is a keystroke, we want to save the new value.
     * This method retrieves the value, verifies it is an acceptable
     * value, and if so it makes the save call.  If it is not valid
     * it will update the state to notify the UI to render an error.
     */
    onChange = (event, data, option) => {
        /** Get the value from either the browser event or the data
         object passed from Semantic UI.  Sometimes Semantic fakes
         browser events by passing their data in the 2nd onChange
         argument since faking it can't have a real browser event
         triggered.  For this reason we need to check different areas
         depending on the field type. */
        let value = this.props.getValueFromOnChange(event, data, option);

        if (isString(value) && !value.trim().length) {
            value = value.trim();
        }

        // early return if same value
        if (value === this.state.value) {
            return;
        }

        // Create a next state, since we'll update multiple state objects based on
        // logic throughout this method, let's just create an object that gets updated
        // and then call setState once at the end with the full new state.
        let nextState = Object.assign({}, this.state, {value: value, checked: value});

        /** Pass the new value to the validation method.  This will
         return a response containing whether or not the input was valid
         and a reason why it is not valid if applicable. */
        let {failedValidations, failedRules} = this.validate(value);

        /** If we got an invalid response status, update the state to show
         the error and we're done. */

      if (failedValidations.length === 0) {  // If our previous state was invalid, reset
        nextState.isValid = true;
        nextState.validationError = [];
      } else {
        nextState.isValid = false;
        nextState.validationError = failedValidations.map(item => item.error);
      }

      nextState.isSaving = true;
      this.setState(nextState, () => this.persistValue(value, failedRules.length > 0 ? failedRules : null));
    };

    /** Determines if the `value` passed in complies with all of the validation
     rules passed in from `props.validationRules`. */
    validate = (value) => {

        /** If we do have any rules, loop through them and update the status.  If
         one fails, return the response*/
        let failedValidations = [];
        let failedRules = [];

        let rules = isNoU(this.props.validationRules) ? [] : this.props.validationRules.slice();
        if (this.props.required && !rules.find(rule => rule.type === "NotNullOrBlank" || rule.type === "HTMLHasTextContent")) {
            rules.push({
                type: "NotNullOrBlank"
            });
        }

        for (let i = 0; i < rules.length; i++) {
            let response = {status: true};  // By default everything passes since we may not even have a rule
            let rule = rules[i];

            response.status = validationRules[rule.type](value, rule.params, this.props.formProxy);
            if (response.status === false) {
                response.error = rule.error || `errors.validation.${rule.type}`;

                failedRules.push(rule);
                failedValidations.push(response);
            }
        }

        return {failedValidations, failedRules};
    };

    /** Takes the value from our current state and posts it to an api to persist */
    persistValue = (val, errors) => {
        // Somewhere along the way an extra state change is interfering the setState that is using this callback
        // Causing the persisted value to be inaccurate, so we receive a value to set with state as fallback
        let value = val || this.state.value;
        if (this.props.onPersistValue) {
            this.props.onPersistValue(value, this.props.name, errors);
        }
    };

    render() {
        // Omit certain known props we don't want to pass on to
        // FieldComponent, but we do want to pass any other random
        // props that may come in from various input types such
        // as dropdowns passing in optins, etc.
        const newProps = omit(this.props, 'additionalData',
            'errors',
            'validationRules',
            'helpContent',
            'helpHeader',
            'extraContent',
            'showExtraContent',
            'saveUrl',
            'getValueFromOnChange',
            'field',
            'supportsLoading',
            'fieldKey',
            'fieldLabel',
            'control',
            'defaultValue',
            'defaultChecked',
            'formProxy',
            'onPersistValue',
            'multiOptions',
            'onValidationError',
            'onValidationSuccess',
            'usingFallbackValue',
            'linkedValue',
            'linkedErrors',
            'usingLinkedValue',
            'idAssetValue',
            'onValueChanged',
            'isSaving',
            'savedField',
            'valueProjection',
            'sectionIndex', 
            'currentPlatform',
            'asList',
            'placeholderKey',
            'dispatch',
            'getTextLengthCallback',
            'getTextLengthCallback',
            'canCreate',
            'showField',
            'labelKey',
            'sectionKeyPath',
            'reducerKey',
            'helperText'
        );

        const FieldComponent = this.props.control;

        // Can't just rely on local state validation in case of an unknown error from api
        const rulesByType = {};
        forEach(this.props.validationRules, (rule) => {
            rulesByType[rule.type] = rule;
        });

        let allErrors = [];
        if (!this.state.isValid) {
            // Copy errors array for potential modification
            allErrors = this.props.errors && this.props.errors.slice();

            // If state errors are not in prop errors, then add it to error list
            for (let error of this.state.validationError) {
                if (allErrors.findIndex((item) => item.key === error) === -1) {
                    allErrors.push({
                        key: error,
                        type: error.replace("errors.validation.", "")
                    })
                }
            }

            if (this.props.usingLinkedValue) {
                for (let error of this.props.linkedErrors) {
                    if (allErrors.findIndex((item) => item.key === error.key) === -1) {
                        allErrors.push(error);
                    }
                }
            }
        }

        if (includeErrorForComponent(FieldComponent)) {
            newProps.error = allErrors.length > 0;
        }

        if (includeCheckedValueForComponent(FieldComponent)) {
            newProps.checked = this.state.checked;
        }

        if (includeValueForComponent(FieldComponent)) {
            newProps.value = this.props.usingLinkedValue ? this.props.defaultValue : this.state.value;
        }

        const statusIcon = (
            <FieldStatusIcon
              saved={this.props.savedField === true}
              show={true}
              isSaving={this.props.isSaving}
              error={newProps.error}/>
        );

        const fieldLabel = this.props.fieldLabel || (
            // not showing labelKey for multi-platform fields
            this.props.labelKey && (this.props.platform ? this.props.platform === 'MASTER' : true) && (
                <Label basic className="db-field-label">
                  {this.props.required || this.props.required ? <i className="asterisk icon inline"/> : null}
                  <FormattedMessage id={this.props.labelKey} />
                </Label>
            )
        );

        let rootClassName = 'auto-save-field';
        if ( newProps.error ) { rootClassName += ' error'; }
        if ( this.props.className ) { rootClassName += (' ' + this.props.className); }

        return (
            <div className={rootClassName}>
              <Form>
                <Grid columns="equal">
                      {fieldLabel ?
                          <Grid.Row className={`db-field-label-row ${this.props.disabled ? ' disabled ' : ''}`}>
                              {fieldLabel}
                              {statusIcon}
                          </Grid.Row>
                          : null}
                      <Grid.Row className="db-field-input-row">
                          <Grid.Column width={8}>
                              {this.props.multiOptions ? (

                                      <Grid>
                                          <Grid.Row>
                                              {range(this.props.multiOptions.colCount).map((colIndex) => {
                                                  let count = Math.ceil(this.props.multiOptions.options.length / this.props.multiOptions.colCount);
                                                  let groupFinal = reduce(this.props.multiOptions.options, (result, option, index) => {
                                                      let i = index % this.props.multiOptions.colCount;
                                                      if (!result[i]) {
                                                          result[i] = [];
                                                      }
                                                      result[i].push(option);
                                                      return result;
                                                  }, {});

                                                  // Add safety check for empty columns
                                                    const columnOptions = groupFinal[colIndex] || [];

                                                  // Not sure if there is anything else needed for this index
                                                  return (
                                                      <Grid.Column width={this.props.multiOptions.colCount > 1 ? 3 : 8} key={colIndex}>
                                                          {columnOptions.map((option, index) => {

                                                              let multiOptions = this.props.multiOptions;

                                                              // Include checked attribute if the option's value is in the current value's array
                                                              // or the option's value is equal to the current value
                                                              if (includeCheckedValueForComponent(FieldComponent)) {
                                                                  newProps.checked = Array.isArray(newProps.value) ?
                                                                      newProps.value.indexOf(option.value) > -1 : newProps.value === option.value;
                                                              }

                                                              let key = `${index}-${this.props.name}-${option.value}`;
                                                              if (option.key) {
                                                                  // remove field name from newProps since we'll use the key
                                                                  delete newProps.name;
                                                              }

                                                              return (
                                                                  <FieldComponent
                                                                      key={option.key ? (option.key + option.value) : key}
                                                                      disabled={this.props.disabled}
                                                                      name={option.key ? option.key : key}
                                                                      onChange={(event, data) => this.onChange(event, data, option)}
                                                                      label={option.text}
                                                                      {...newProps}
                                                                  />
                                                              );
                                                          })}
                                                      </Grid.Column>
                                                  );
                                              })}

                                          </Grid.Row>
                                      </Grid>)
                                  :
                                  <React.Fragment>
                                    <FieldComponent disabled={this.props.disabled}
                                                    className={this.props.usingFallbackValue ? 'fallback-value' : ''}
                                                    name={this.props.name}
                                                    onChange={this.onChange}
                                                    
                                                    {...newProps}
                                    />
    
                                  </React.Fragment>
                              }

                              {allErrors.map((error) => {
                                  let params = rulesByType[error.type] ? rulesByType[error.type].params : {};

                                  if (error.type === "NANDGate") {
                                    return (
                                      <Label basic className="db-error-label" key={error.key}>
                                          <FormattedMessage id={error.key} values={{otherField: params.otherField}}/>&nbsp;
                                          {error.invalidValues ? (
                                              <FormattedMessage id='errors.validation.invalid_values'
                                                                values={{invalidValues: error.invalidValues}}/>
                                          ) : ""}
                                      </Label>
                                    );
                                  }

                                  return (
                                      <Label basic className="db-error-label" key={error.key}>
                                          <FormattedMessage id={error.key} values={{...params}}/>&nbsp;
                                          {error.invalidValues ? (
                                              <FormattedMessage id='errors.validation.invalid_values'
                                                                values={{invalidValues: error.invalidValues}}/>
                                          ) : ""}
                                      </Label>
                                  );
                              })}
                              {/* marginTop: "-14px",  */}
                              { this.props.helperText && (!!!this.props.hideHelperText) && <div style={{marginBottom: "6px", fontSize: "14px"}}>{this.props.helperText}</div>}
                          </Grid.Column>
                          {this.props.helpContent ?
                              <Grid.Column width={1} verticalAlign="middle" className="db-tooltip">


                                  <div className="popup-scroll-container">

                                      <Tooltip
                                          // options
                                          html={(
                                              <div style={{'maxWidth': '600px', 'maxHeight': '600px', 'overflow': 'auto'}}>
                                                {isFunction(this.props.helpContent) ? this.props.helpContent(this.props.formProxy) : this.props.helpContent}
                                              </div>
                                          )}
                                          arrow="true"
                                          position="right-top"
                                          theme="light"
                                          trigger="click"
                                          style={{width: '40%'}}
                                      >
                                          <Icon color="blue" name="info circle"/>
                                      </Tooltip>
                                  </div>

                              </Grid.Column>
                              : ""
                          }
                          {this.props.showExtraContent && (
                              <Grid.Column verticalAlign='middle'>
                                {isFunction(this.props.extraContent) ? this.props.extraContent(this.props.formProxy) : this.props.extraContent}
                              </Grid.Column>
                          )}
                      </Grid.Row>
                  </Grid>
              </Form>
            </div>
        );
    }
}

export default AutoSaveField;

AutoSaveField.propTypes = {

    /** Additional parameters that should be passed along with the persist api call */
    additionalData: PropTypes.object,

    /** The Form Field Component to Render */
    control: PropTypes.any.isRequired,  // TODO: Better validation on this such as element

    /** The value to prepopulate this field with. */
    defaultValue: PropTypes.any,

    /** True/false if the user should be able to edit the content of this field. */
    disabled: PropTypes.bool,

    /** A descriptive string above each field such as 'Title' or 'Description' */
    fieldLabel: PropTypes.any,

    /** Returns the current value of this field based on its onChange browser event
     or data param.  This should support the definition of getValueFromOnChange(evene,data) */
    getValueFromOnChange: PropTypes.func,

    /** The name of the field, this is what gets submitted to the api call */
    name: PropTypes.string.isRequired,

    /** The url to persist the field value to.  Example '/break/save' */
    saveUrl: PropTypes.string,

    /** A list of rules that can be executed to determine if the current field
     value is consiered valid input. */
    validationRules: PropTypes.arrayOf(PropTypes.object),

    onValidationError: PropTypes.func,

    onValidationSuccess: PropTypes.func,

    onPersistValue: PropTypes.func.isRequired,

    errors: PropTypes.arrayOf(PropTypes.object),

    linkedValue: PropTypes.any,

    linkedErrors: PropTypes.arrayOf(PropTypes.object),

    /**
     * Flag for if the render input should be marked as using a fallback value, by assigning a css class to the input,
     */
    usingLinkedValue: PropTypes.bool,

    /**
     * Flag to say this field is currently being saved
     */
    isSaving: PropTypes.bool
};

AutoSaveField.defaultProps = {
    disabled: false,
    onValidationError: EmptyFunc,
    onValidationSuccess: EmptyFunc,
    errors: [],
    linkedErrors: []
};



// WEBPACK FOOTER //
// ./public/components/forms/AutoSaveFormFields/abstract/AutoSaveField.jsx